export type CalendarSyncAction =
  | 'sync-started'
  | 'sync-completed'
  | 'sync-error'
  | 'event-updated'
  | 'event-deleted'
  | 'bot-status-changed';

type SyncStartedPayload = { syncType: 'incremental' | 'full' };
type SyncCompletedPayload = { syncType: string; lastSyncAt: string; eventsChanged: number };
type BotStatusPayload = { eventId: string; botInviteStatus: string; botInviteError?: string };

export class CalendarSyncSocketAction {
  private socket: WebSocket | null = null;
  private callbacks: Record<string, ((...args: unknown[]) => void)[]> = {};
  private userId: string;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly HEARTBEAT_INTERVAL_MS = 30_000;
  private static readonly PONG_TIMEOUT_MS = 10_000;

  /** SNAP-2309: the session cookie travels with the handshake; no token is put in the URL. */
  constructor(baseUrl: string, userId: string) {
    this.userId = userId;
    this.socket = new WebSocket(`${baseUrl}/calendar-sync`);
    this.setupHandlers();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send('ping');
        this.pongTimer = setTimeout(() => {
          // No pong received — connection is dead, force close to trigger reconnect
          this.socket?.close();
        }, CalendarSyncSocketAction.PONG_TIMEOUT_MS);
      }
    }, CalendarSyncSocketAction.HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private setupHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.socket?.send(JSON.stringify({ type: 'start', userId: this.userId }));
      this.startHeartbeat();
      this.emit('connect');
    };

    this.socket.onmessage = (event: MessageEvent) => {
      const raw = event.data as string;

      if (raw === 'ping') {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send('pong');
        }
        return;
      }

      if (raw === 'pong') {
        // Server responded to our heartbeat ping — connection is alive
        if (this.pongTimer) {
          clearTimeout(this.pongTimer);
          this.pongTimer = null;
        }
        return;
      }

      try {
        const message = JSON.parse(raw);
        if (message.type === 'update' && message.data?.action) {
          this.routeMessage(message.data);
        }
      } catch {
        // Ignore unparseable messages
      }
    };

    this.socket.onclose = () => {
      this.stopHeartbeat();
      this.emit('disconnect');
    };

    this.socket.onerror = () => {
      // Error will trigger onclose
    };
  }

  private routeMessage(data: Record<string, unknown>): void {
    const action = data.action as CalendarSyncAction;

    switch (action) {
      case 'sync-started':
        this.emit('syncStarted', { syncType: data.syncType } as SyncStartedPayload);
        break;
      case 'sync-completed':
        this.emit('syncCompleted', {
          syncType: data.syncType,
          lastSyncAt: data.lastSyncAt,
          eventsChanged: data.eventsChanged,
        } as SyncCompletedPayload);
        break;
      case 'sync-error':
        this.emit('syncError', data.error as string);
        break;
      case 'event-updated':
        this.emit('eventUpdated', data.event);
        break;
      case 'event-deleted':
        this.emit('eventDeleted', data.providerEventId as string);
        break;
      case 'bot-status-changed':
        this.emit('botStatusChanged', {
          eventId: data.eventId,
          botInviteStatus: data.botInviteStatus,
          botInviteError: data.botInviteError,
        } as BotStatusPayload);
        break;
    }
  }

  private emit(event: string, ...args: unknown[]): void {
    const handlers = this.callbacks[event];
    if (handlers) {
      for (const handler of handlers) {
        handler(...args);
      }
    }
  }

  private on(event: string, handler: (...args: unknown[]) => void): void {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    this.callbacks[event].push(handler);
  }

  onSyncStarted(cb: (payload: SyncStartedPayload) => void): void {
    this.on('syncStarted', cb as (...args: unknown[]) => void);
  }

  onSyncCompleted(cb: (payload: SyncCompletedPayload) => void): void {
    this.on('syncCompleted', cb as (...args: unknown[]) => void);
  }

  onSyncError(cb: (error: string) => void): void {
    this.on('syncError', cb as (...args: unknown[]) => void);
  }

  onEventUpdated(cb: (event: unknown) => void): void {
    this.on('eventUpdated', cb as (...args: unknown[]) => void);
  }

  onEventDeleted(cb: (providerEventId: string) => void): void {
    this.on('eventDeleted', cb as (...args: unknown[]) => void);
  }

  onBotStatusChanged(cb: (payload: BotStatusPayload) => void): void {
    this.on('botStatusChanged', cb as (...args: unknown[]) => void);
  }

  onConnect(cb: () => void): void {
    this.on('connect', cb as (...args: unknown[]) => void);
  }

  onDisconnect(cb: () => void): void {
    this.on('disconnect', cb as (...args: unknown[]) => void);
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }
}
